import { useEffect, useCallback } from "react";
import { createPkce } from "../helpers/pkce";

const providerBase = process.env.REACT_APP_COGNITO_SOCIAL_PROVIDER;
const redirectUri = process.env.REACT_APP_REDIRECT_URI;
const scope = "aws.cognito.signin.user.admin email openid phone profile";
const tokenEndpoint = `${providerBase}/oauth2/token`;

const getClientId = () => {
  const cc = (localStorage.getItem("countryCode") || "TR").toUpperCase();
  return cc === "DE"
    ? process.env.REACT_APP_CLIENT_ID_GLOBAL
    : process.env.REACT_APP_CLIENT_ID;
};

async function exchangeCodeForTokens(code, state) {
  const savedState = sessionStorage.getItem("oauth_state");
  const codeVerifier = sessionStorage.getItem("pkce_verifier");
  const client_id = sessionStorage.getItem("oauth_client_id") || getClientId();
  const ru =
    sessionStorage.getItem("oauth_redirect_uri") || String(redirectUri);

  if (!codeVerifier || !savedState || state !== savedState) {
    throw new Error("PKCE/state validation failed");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id,
    code,
    redirect_uri: ru,
    code_verifier: codeVerifier,
  });

  const res = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) throw new Error(`Token HTTP ${res.status}: ${await res.text()}`);
  const data = await res.json();

  [
    "pkce_verifier",
    "oauth_state",
    "oauth_client_id",
    "oauth_redirect_uri",
  ].forEach((k) => sessionStorage.removeItem(k));
  return data;
}

export function useCognitoOAuth({
  remember,
  authenticateUser,
  onSignedIn,
  listenLegacy = true,
} = {}) {
  const persistAndAuthenticate = useCallback(
    async (tokens) => {
      const { access_token, id_token, refresh_token } = tokens || {};
      if (!access_token || !id_token) throw new Error("Missing tokens");

      const storage = remember ? localStorage : sessionStorage;
      storage.setItem("access_token", access_token);
      storage.setItem("id_token", id_token);
      if (refresh_token) storage.setItem("refresh_token", refresh_token);

      await authenticateUser(
        access_token,
        id_token,
        refresh_token || undefined
      );
      if (typeof onSignedIn === "function") onSignedIn();
    },
    [remember, authenticateUser, onSignedIn]
  );

  const buildAuthorizeUrl = useCallback(async (identityProvider) => {
    const clientId = getClientId();
    const { code_challenge, state } = await createPkce();
    sessionStorage.setItem("oauth_client_id", String(clientId));
    sessionStorage.setItem("oauth_redirect_uri", String(redirectUri));

    const p = new URLSearchParams({
      identity_provider: identityProvider,
      redirect_uri: String(redirectUri),
      response_type: "code",
      client_id: String(clientId),
      scope,
      code_challenge,
      code_challenge_method: "S256",
      state,
    });
    return `${providerBase}/oauth2/authorize?${p.toString()}`;
  }, []);

  const openProviderPopup = useCallback(
    async (provider) => {
      const isMobile = () =>
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const url = await buildAuthorizeUrl(provider);
      if (isMobile()) {
        window.location.assign(url);
        return;
      }
      const w = 500,
        h = 600;
      const left = window.screen.width / 2 - w / 2;
      const top = window.screen.height / 2 - h / 2;
      const popup = window.open(
        url,
        "oauth_popup",
        `width=${w},height=${h},top=${top},left=${left}`
      );

      if (!popup) {
        window.location.assign(url);
      }
    },
    [buildAuthorizeUrl]
  );

  useEffect(() => {
    const onMessage = async (ev) => {
      if (ev.origin !== window.location.origin) return;

      if (ev.data?.type === "oauth-code") {
        try {
          const { code, state } = ev.data.payload || {};
          const tokens = await exchangeCodeForTokens(code, state);
          await persistAndAuthenticate(tokens);
        } catch (e) {
          console.error("OAuth exchange (opener) failed:", e);
        }
        return;
      }

      if (listenLegacy && ev.data?.type === "cognito-token") {
        try {
          await persistAndAuthenticate(ev.data.payload || {});
        } catch (e) {
          console.error("OAuth (legacy) failed:", e);
        }
        return;
      }

      if (ev.data?.type === "oauth-error") {
        console.error("OAuth error:", ev.data.error);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [persistAndAuthenticate, listenLegacy]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const code = qs.get("code");
    const state = qs.get("state");
    if (!code || !state) return;

    (async () => {
      try {
        if (window.opener) {
          window.opener.postMessage(
            { type: "oauth-code", payload: { code, state } },
            window.location.origin
          );
          window.close();
          return;
        }
        const tokens = await exchangeCodeForTokens(code, state);
        await persistAndAuthenticate(tokens);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        console.error("OAuth exchange (same-window) failed:", e);
      }
    })();
  }, [persistAndAuthenticate]);

  return { openProviderPopup };
}

export const logoutFromAll = (isNavigate) => {
  const domain = process.env.REACT_APP_COGNITO_SOCIAL_PROVIDER;
  const clientId = process.env.REACT_APP_CLIENT_ID;
  const appRoot = process.env.REACT_APP_REDIRECT_URI;

  const logoutUrl =
    `${domain}/logout?client_id=${encodeURIComponent(String(clientId))}` +
    `&logout_uri=${encodeURIComponent(appRoot)}`;

  const last = (sessionStorage.getItem("last_idp") || "").toLowerCase();
  let idpLogout = "";

  if (last.includes("google")) {
    idpLogout = "https://accounts.google.com/logout";
  } else if (last.includes("apple")) {
    idpLogout = "https://appleid.apple.com/auth/logout";
  } else if (last.includes("facebook")) {
    idpLogout = "https://www.facebook.com/logout.php";
  }

  let win = null;
  if (idpLogout) {
    win = window.open(idpLogout, "_blank", "width=10,height=10");
  }

  setTimeout(() => {
    if (win && !win.closed) win.close();
    if(isNavigate) { window.location.replace(logoutUrl); }
  }, 1000);
};
